import { EditOutlined, DeleteOutline } from '@mui/icons-material';
import { Button, Grid } from '@mui/material';
import { EssealDataTable } from 'esseal-data-table';
import React, { useState } from 'react';

import companyHolidaysColumns from './companyHolidaysColumns';
import HolidayModal from './HolidayModal';

import { type CompanyHoliday } from '@/Contexts/Settings/Context';

interface TableRow extends CompanyHoliday {
  id: string;
  index: number;
}

interface HolidaysTableProps {
  holidays: CompanyHoliday[];
  onChange: (updatedHolidays: CompanyHoliday[]) => void;
  titlePrefix?: string;
}

const HolidaysTable: React.FC<HolidaysTableProps> = ({ holidays, onChange, titlePrefix = 'Company' }) => {
  const [selectedHoliday, setSelectedHoliday] = useState<TableRow | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const rows: TableRow[] = holidays.map((holiday, index) => ({
    ...holiday,
    id: `${holiday.date}_${holiday.shift}_${index}`,
    index,
  }));

  const openCreateModal = () => {
    setSelectedHoliday(null);
    setIsModalOpen(true);
  };

  const openEditModal = (row: TableRow) => {
    setSelectedHoliday(row);
    setIsModalOpen(true);
  };

  const handleSaveHoliday = (savedHoliday: CompanyHoliday) => {
    const updated = [...holidays];
    if (selectedHoliday !== null) {
      updated[selectedHoliday.index] = savedHoliday;
    } else {
      updated.push(savedHoliday);
    }

    updated.sort((a, b) => a.date.localeCompare(b.date));
    onChange(updated);
  };

  const handleDeleteHoliday = (index: number) => {
    const updated = holidays.filter((_, i) => i !== index);
    onChange(updated);
  };

  return (
    <Grid container spacing={2} size={12} height="100%">
      <Grid size={12} sx={{ width: '100%' }}>
        <EssealDataTable
          rows={rows}
          columns={companyHolidaysColumns}
          checkboxSelection={false}
          pagination={true}
          pageSize={10}
          maxVisibleActions={2}
          toolbar={
            <Button variant="contained" size="small" onClick={openCreateModal}>
              + New
            </Button>
          }
          rowActions={(row: TableRow) => [
            {
              label: 'Edit',
              icon: <EditOutlined fontSize="small" color="primary" />,
              tooltip: 'Edit Holiday',
              onClick: () => openEditModal(row),
            },
            {
              label: '',
              icon: <DeleteOutline fontSize="small" color="error" />,
              tooltip: 'Delete Holiday',
              onClick: () => handleDeleteHoliday(row.index),
            },
          ]}
        />
      </Grid>

      {isModalOpen && (
        <HolidayModal
          title={selectedHoliday ? `Edit ${titlePrefix} Holiday` : `Add ${titlePrefix} Holiday`}
          handleClose={() => {
            setIsModalOpen(false);
            setSelectedHoliday(null);
          }}
          initialHoliday={selectedHoliday}
          onSave={handleSaveHoliday}
        />
      )}
    </Grid>
  );
};

export default HolidaysTable;
